'use client';

import { type FormEvent, type ReactElement, useCallback, useRef } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useFetcher } from 'react-router';
import { Button, SingleInput, Typography } from '@/shared-ui/atoms';
import { cn } from '@/lib/utils';
import { showToast } from '@/features/toast';
import { useFetcherEffect } from '@/hooks/use-fetcher-effect';
import { resourceRoutes } from '@/route-paths';
import {
    externalLinkClassName,
    NEWSLETTER_COOKIE_POLICY_URL,
    NEWSLETTER_PRIVACY_URL,
    NEWSLETTER_TERMS_URL,
} from './newsletter-form.constants';

type NewsletterSignupResponse = {
    success?: boolean;
    error?: string;
};

export interface NewsletterFormProps {
    className?: string;
}

export function NewsletterForm({ className }: NewsletterFormProps): ReactElement {
    const { t } = useTranslation('globalFooter');
    const inputRef = useRef<HTMLInputElement | null>(null);
    const fetcher = useFetcher<NewsletterSignupResponse>();
    const isSubmitting = fetcher.state !== 'idle';

    useFetcherEffect<NewsletterSignupResponse>(fetcher, {
        onSuccess: () => {
            showToast({
                title: t('newsletter.success'),
                description: '',
                variant: 'success',
            });
        },
        onError: (error) => {
            const message = Array.isArray(error) ? error.join(', ') : error;
            showToast({
                title: message,
                description: '',
                variant: 'error',
            });
        },
    });

    const handleSubmit = useCallback(
        (event: FormEvent<HTMLFormElement>) => {
            event.preventDefault();
            const email = inputRef.current?.value?.trim();
            if (!email) {
                return;
            }

            const formData = new FormData();
            formData.append('email', email);

            void fetcher.submit(formData, {
                method: 'POST',
                action: resourceRoutes.newsletterSignup,
            });
        },
        [fetcher]
    );

    return (
        <section
            className={cn('flex w-full min-w-0 max-w-full flex-col gap-4 overflow-x-clip', className)}
            data-slot="newsletter-form">
            <Typography variant="h4" weight="regular" className="text-primary-foreground">
                {t('newsletter.title')}
            </Typography>

            <form onSubmit={handleSubmit} className="flex w-full min-w-0 max-w-full flex-col gap-2">
                <SingleInput
                    ref={inputRef}
                    type="email"
                    name="email"
                    autoComplete="email"
                    inputMode="email"
                    required
                    disabled={isSubmitting}
                    placeholder={t('newsletter.emailPlaceholder')}
                    aria-label={t('newsletter.emailPlaceholder')}
                    className="w-full min-w-0 max-w-full truncate bg-background"
                />

                <div className="flex w-full min-w-0 max-w-full flex-col gap-4 md:flex-row md:items-center md:gap-6">
                    <Button
                        type="submit"
                        variant="secondary"
                        size="lg"
                        disabled={isSubmitting}
                        className="w-full shrink-0 md:w-auto md:self-start">
                        {t('newsletter.subscribeButton')}
                    </Button>

                    <Typography
                        variant="small"
                        weight="light"
                        className="text-primary-foreground text-[12px] tracking-[0.015rem]">
                        <Trans
                            ns="globalFooter"
                            i18nKey={'newsletter.consent' as never}
                            components={{
                                terms: (
                                    <a
                                        href={NEWSLETTER_TERMS_URL}
                                        className={externalLinkClassName}
                                        target="_blank"
                                        rel="noopener noreferrer">
                                        {t('newsletter.termsLink')}
                                    </a>
                                ),

                                privacy: (
                                    <a
                                        href={NEWSLETTER_PRIVACY_URL}
                                        className={externalLinkClassName}
                                        target="_blank"
                                        rel="noopener noreferrer">
                                        {t('newsletter.privacyLink')}
                                    </a>
                                ),

                                cookies: (
                                    <a
                                        href={NEWSLETTER_COOKIE_POLICY_URL}
                                        className={externalLinkClassName}
                                        target="_blank"
                                        rel="noopener noreferrer">
                                        {t('newsletter.cookiesLink')}
                                    </a>
                                ),
                            }}
                        />
                    </Typography>
                </div>
            </form>
        </section>
    );
}
